import { SWR_KEYS } from '@bulknutrients/bulk-shared-lib/constants';

import type { Customer } from '@bulknutrients/bulk-shared-lib/interfaces/Customer';
import { log, logBreadcrumb } from '@lib/logs';
import useSWR from 'swr';

const fetcher = async (input: RequestInfo, init?: RequestInit) => {
  const response = await fetch(input, init);
  const res = await response.json();

  if (!response.ok) {
    if (response.status === 400 || response.status === 401) {
      return null;
    }

    const error = new Error('Unable to fetch customer');
    (error as Error & { status?: number }).status = response.status;
    throw error;
  }

  if (res.data) {
    return res.data;
  }

  return null;
};

export function useCustomer(): {
  customer: Customer | null;
  isLoading: boolean;
} {
  const { data: customer, isLoading } = useSWR<Customer | null>(
    SWR_KEYS.customer,
    fetcher,
    {
      dedupingInterval: 500,
      revalidateIfStale: false,
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
      shouldRetryOnError: false,
      onError: (error) => {
        logBreadcrumb({
          category: 'hooks',
          message: 'useCustomer request',
          level: 'error',
        });

        log({ error: error, location: 'useCustomer' });
      },
    }
  );

  return { customer: customer ?? null, isLoading };
}
